// Memoized Recommendation Service - Cache recommendation results
// Prevents expensive recalculations on every render (Duolingo-style)

import { logger } from '../utils/logger';
import { aiCoach } from './aiCoach';
import type { TestRecommendation } from './aiCoach';

interface CachedRecommendation {
  recommendation: TestRecommendation;
  testHistoryHash: string; // Hash of test history to detect changes
  timestamp: number;
  expiresAt: number;
}

class MemoizedRecommendationsService {
  private cache: Map<string, CachedRecommendation> = new Map();
  private readonly CACHE_TTL = 60000; // 1 minute cache
  private readonly MAX_CACHE_SIZE = 10; // Max 10 cached recommendations

  /**
   * Check if a cached recommendation exists and is valid (fast check, no calculation)
   */
  hasCachedRecommendation(userId: string): boolean {
    const cacheKey = this.getCacheKey(userId);
    const cached = this.cache.get(cacheKey);
    return cached ? this.isCacheValid(cached) : false;
  }

  /**
   * Get cached recommendation without calculating (returns null if not cached)
   */
  getCachedRecommendation(userId: string): TestRecommendation | null {
    const cacheKey = this.getCacheKey(userId);
    const cached = this.cache.get(cacheKey);
    if (cached && this.isCacheValid(cached)) {
      return cached.recommendation;
    }
    return null;
  }

  /**
   * Get recommendation with memoization
   * Only recalculates if test history changed or cache expired
   */
  getRecommendation(
    userId: string,
    translate?: (key: string) => string
  ): TestRecommendation {
    const cacheKey = this.getCacheKey(userId);
    const cached = this.cache.get(cacheKey);

    // Check if cache is valid
    if (cached && this.isCacheValid(cached)) {
      logger.debug('Using cached recommendation for user:', userId);
      return cached.recommendation;
    }

    // Cache miss or invalid - calculate new recommendation
    logger.debug('Calculating new recommendation for user:', userId);
    const recommendation = aiCoach.getTopRecommendation(translate);
    
    // Get test history hash to detect changes
    const testHistoryHash = this.getTestHistoryHash(userId);

    // Cache the result
    this.cache.set(cacheKey, {
      recommendation,
      testHistoryHash,
      timestamp: Date.now(),
      expiresAt: Date.now() + this.CACHE_TTL
    });

    // Clean up old cache entries
    this.cleanupCache();

    return recommendation;
  }

  /**
   * Invalidate cache for a user (call when test history changes)
   */
  invalidate(userId: string): void {
    const cacheKey = this.getCacheKey(userId);
    this.cache.delete(cacheKey);
    logger.debug('Invalidated recommendation cache for user:', userId);
  }

  /**
   * Invalidate all caches
   */
  invalidateAll(): void {
    this.cache.clear();
    logger.debug('Invalidated all recommendation caches');
  }

  /**
   * Check if cache is valid
   */
  private isCacheValid(cached: CachedRecommendation): boolean {
    // Check if expired
    if (Date.now() > cached.expiresAt) {
      return false;
    }

    // Check if test history changed
    const currentHash = this.getTestHistoryHash(this.getUserIdFromCache(cached));
    if (currentHash !== cached.testHistoryHash) {
      return false;
    }

    return true;
  }

  /**
   * Get cache key for user
   */
  private getCacheKey(userId: string): string {
    return `recommendation_${userId}`;
  }

  /**
   * Get test history hash to detect changes
   */
  private getTestHistoryHash(_userId: string): string {
    const testHistory = aiCoach.getTestHistory();
    // Simple hash of test history length and last test ID
    const historyString = JSON.stringify(testHistory.map(t => ({
      testId: t.testId,
      date: t.date
    })));
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < historyString.length; i++) {
      const char = historyString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return `${testHistory.length}_${Math.abs(hash)}`;
  }

  /**
   * Get user ID from cache (for hash comparison)
   */
  private getUserIdFromCache(_cached: CachedRecommendation): string {
    // Extract from cache key if needed, or use a default
    // For now, we'll use a simple approach
    return 'default'; // This could be improved
  }

  /**
   * Clean up old cache entries
   */
  private cleanupCache(): void {
    if (this.cache.size <= this.MAX_CACHE_SIZE) {
      return;
    }

    // Remove oldest entries
    const entries = Array.from(this.cache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    const toRemove = entries.slice(0, entries.length - this.MAX_CACHE_SIZE);
    toRemove.forEach(([key]) => {
      this.cache.delete(key);
    });
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    size: number;
    maxSize: number;
    hitRate: number;
  } {
    return {
      size: this.cache.size,
      maxSize: this.MAX_CACHE_SIZE,
      hitRate: 0 // Could track this if needed
    };
  }
}

export const memoizedRecommendations = new MemoizedRecommendationsService();















